import { useMemo } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { useViewStore } from '../state/commandAuxStores';
import { ISO_PLANE_ANGLES, type IsoPlane } from '../core/drafting/isoPlane';
import { useViewportThemeColors } from '../hooks/useThemeInk';

/**
 * Isometric drafting grid (Faz 7.1 — OCS grid iso parity).
 *
 * While ISODRAFT is on, the two axis families of the active iso plane draw
 * as full-plane parallel lines over the ground (OCS draws the grid with the
 * iso pair on model and paper). Spacing follows the classic 1-2-5 ladder at
 * the same on-screen density floor as AdaptiveCadGrid (12 px), capped at a
 * fixed line budget per family. One LineSegments draw call; zero cost while
 * isodraft is off.
 */

const MIN_CELL_PX = 12;
/** Half-extent of the drawn grid around the origin, in world units. */
const GRID_EXTENT = 250;
/** Line budget per family (density cap at far zoom). */
const MAX_LINES_PER_FAMILY = 90;

/** Smallest value >= x on the 1-2-5 decade ladder. */
function nextLadder(x: number): number {
    const clamped = Math.min(Math.max(x, 1e-6), 1e6);
    const base = Math.pow(10, Math.floor(Math.log10(clamped)));
    for (const m of [1, 2, 5]) {
        if (m * base >= clamped - base * 1e-6) return m * base;
    }
    return 10 * base;
}

function worldPerPixel(camera: THREE.Camera, viewportHeight: number): number {
    if (camera instanceof THREE.OrthographicCamera) return 1 / (camera.zoom || 1);
    const { fov, near, position } = camera as THREE.PerspectiveCamera;
    const dist = Math.abs(position.z);
    if (dist <= near) return Number.POSITIVE_INFINITY;
    return (2 * dist * Math.tan((fov * Math.PI) / 360)) / viewportHeight;
}

function buildIsoGeometry(angle1: number, angle2: number, spacing: number): THREE.BufferGeometry {
    const pts: number[] = [];
    const L = GRID_EXTENT * 1.5;
    ([angle1, angle2] as const).forEach((angleDeg) => {
        const rad = (angleDeg * Math.PI) / 180;
        const ux = Math.cos(rad), uy = Math.sin(rad);
        // Perpendicular ladder across the plane family.
        const nx = -uy, ny = ux;
        const count = Math.min(MAX_LINES_PER_FAMILY, Math.round(GRID_EXTENT / spacing));
        for (let i = -Math.floor(count / 2); i <= Math.floor(count / 2); i++) {
            const ox = nx * spacing * i, oy = ny * spacing * i;
            pts.push(ox - ux * L, oy - uy * L, 0, ox + ux * L, oy + uy * L, 0);
        }
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    return geo;
}

export function IsometricGridOverlay() {
    const isoDrafting = useViewStore((s) => s.isoDrafting);
    const isoplane = useViewStore((s) => s.isoplane);
    const camera = useThree((s) => s.camera);
    const size = useThree((s) => s.size);
    // Theme-driven grid ink (the minor-grid token of the active mode sheet).
    const palette = useViewportThemeColors('2d');

    const geometry = useMemo(() => {
        if (!isoDrafting) return null;
        const wpp = worldPerPixel(camera, size.height);
        if (!Number.isFinite(wpp) || wpp <= 0) return null;
        let spacing = nextLadder(MIN_CELL_PX * wpp);
        // Cap the ladder growth so far-zoom stays inside the line budget.
        while (GRID_EXTENT / spacing > MAX_LINES_PER_FAMILY) spacing *= 2;
        const plane: IsoPlane = isoplane === 'off' ? 'left' : isoplane;
        const [a1, a2] = ISO_PLANE_ANGLES[plane];
        return buildIsoGeometry(a1, a2, spacing);
    }, [isoDrafting, isoplane, camera, size.height]);

    if (!geometry) return null;
    return (
        <lineSegments geometry={geometry} renderOrder={-1}>
            <lineBasicMaterial color={palette.gridMinor} transparent opacity={0.5} depthWrite={false} />
        </lineSegments>
    );
}
